import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield, UserPlus, Trash2, Loader2, AlertCircle, CheckCircle2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Page /admin/allowlist — réservée MJSA INVEST (is_platform_admin).
 * Gère la liste des emails autorisés à se connecter à la beta privée.
 *   - Emails du .env : lecture seule (nécessitent redéploiement)
 *   - Emails Mongo : ajout/retrait en 1 clic, effet immédiat
 */
export default function AllowlistAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/allowlist");
      setData(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        setData({ error: "Accès réservé à l'éditeur de PitBoard." });
      } else {
        setData({ error: "Erreur de chargement." });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    setSaving(true);
    try {
      await api.post("/platform/allowlist", { email, note: newNote.trim() });
      setNewEmail("");
      setNewNote("");
      toast.success(`${email} autorisé — effet immédiat`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Retirer ${email} de l'allowlist ?\nCette personne ne pourra plus se connecter (sauf si son email est dans .env ou team_invitations).`)) return;
    try {
      await api.delete(`/platform/allowlist/${encodeURIComponent(email)}`);
      toast.success(`${email} retiré`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur suppression");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm" style={{ color: "var(--ink-soft)" }}>
        <Loader2 size={16} className="animate-spin" /> Chargement…
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="p-6">
        <div
          className="rounded-lg border p-4 flex items-center gap-3"
          style={{ background: "rgba(225,6,0,0.08)", borderColor: "rgba(225,6,0,0.4)", color: "#E10600" }}
        >
          <Lock size={20} />
          <div>{data.error}</div>
        </div>
      </div>
    );
  }

  const runtime = data?.runtime_entries || [];
  const envList = data?.env_entries || [];
  const betaEnabled = data?.beta_enabled;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto animate-in">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={22} style={{ color: "var(--pit-yellow)" }} />
          <h1 className="font-display text-2xl sm:text-3xl" style={{ fontWeight: 700, color: "var(--chalk)" }}>
            Beta Allowlist
          </h1>
        </div>
        <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
          Gère en direct les emails autorisés à se connecter pendant la bêta privée.
          {betaEnabled ? (
            <span className="ml-1" style={{ color: "#22C55E" }}>· Bêta privée ACTIVE ✓</span>
          ) : (
            <span className="ml-1" style={{ color: "var(--grey)" }}>· Bêta désactivée (accès libre)</span>
          )}
        </p>
      </header>

      {/* Formulaire d'ajout */}
      <section
        className="card p-5 mb-6"
        style={{ border: "1px solid var(--steel)" }}
        data-testid="allowlist-add-form"
      >
        <h2 className="font-display text-lg mb-3 flex items-center gap-2" style={{ fontWeight: 600 }}>
          <UserPlus size={18} style={{ color: "var(--pit-yellow)" }} />
          Ajouter un beta-testeur
        </h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--ink-soft)" }}>
              EMAIL *
            </label>
            <input
              type="text"
              inputMode="email"
              autoComplete="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="prenom.nom@gmail.com"
              className="input w-full"
              data-testid="allowlist-email-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--ink-soft)" }}>
              NOTE (optionnel — pour te souvenir qui c'est)
            </label>
            <input
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Ex. Insta @artisan_ludo · Peintre Marseille"
              className="input w-full"
              maxLength={200}
              data-testid="allowlist-note-input"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
            data-testid="allowlist-submit"
          >
            {saving ? (
              <><Loader2 size={14} className="animate-spin" /> Ajout…</>
            ) : (
              <><UserPlus size={14} /> Autoriser l'accès</>
            )}
          </button>
        </form>
      </section>

      {/* Liste runtime (modifiable) */}
      <section className="mb-6">
        <h2 className="font-display text-lg mb-3" style={{ fontWeight: 600 }}>
          Beta-testeurs autorisés ({runtime.length})
        </h2>
        {runtime.length === 0 ? (
          <div
            className="rounded-lg border-2 border-dashed p-6 text-center"
            style={{ borderColor: "var(--steel)", color: "var(--grey)" }}
            data-testid="allowlist-empty"
          >
            <Mail size={24} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun beta-testeur ajouté pour l&apos;instant.</p>
            <p className="text-xs mt-1">Ajoute-en un ci-dessus pour lui donner accès instantanément.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="allowlist-runtime-list">
            {runtime.map((r) => (
              <li
                key={r.email}
                className="card p-3 flex items-start justify-between gap-3"
                data-testid={`allowlist-entry-${r.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CheckCircle2 size={14} style={{ color: "#22C55E", flexShrink: 0 }} />
                    <span className="font-mono text-sm truncate" style={{ color: "var(--chalk)" }}>{r.email}</span>
                  </div>
                  {r.note && (
                    <div className="text-xs mt-1 pl-6" style={{ color: "var(--ink-soft)" }}>
                      {r.note}
                    </div>
                  )}
                  <div className="text-[11px] mt-1 pl-6" style={{ color: "var(--grey)" }}>
                    Ajouté {r.added_at?.slice(0, 10)} par {r.added_by || "—"}
                  </div>
                </div>
                <button
                  onClick={() => remove(r.email)}
                  className="btn btn-outline shrink-0"
                  style={{ padding: "6px 10px", minHeight: 32 }}
                  data-testid={`allowlist-remove-${r.email}`}
                  title="Retirer de l'allowlist"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Liste .env (readonly) */}
      {envList.length > 0 && (
        <section>
          <h2 className="font-display text-base mb-2 flex items-center gap-2" style={{ fontWeight: 600, color: "var(--ink-soft)" }}>
            <Lock size={14} />
            Emails permanents (.env)
          </h2>
          <p className="text-xs mb-3" style={{ color: "var(--grey)" }}>
            Ces emails sont autorisés en permanence via le fichier de configuration. Pour les modifier, il faut passer par un redéploiement.
          </p>
          <ul className="space-y-1" data-testid="allowlist-env-list">
            {envList.map((email) => (
              <li
                key={email}
                className="text-xs font-mono px-3 py-2 rounded flex items-center gap-2"
                style={{ background: "rgba(255,255,255,0.03)", color: "var(--ink-soft)" }}
              >
                <Lock size={11} style={{ color: "var(--grey)" }} />
                {email}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Info bas de page */}
      <div
        className="mt-8 rounded-lg p-4 flex items-start gap-3 text-xs"
        style={{ background: "rgba(255,196,0,0.05)", border: "1px solid rgba(255,196,0,0.2)", color: "var(--ink-soft)" }}
      >
        <AlertCircle size={14} style={{ color: "var(--pit-yellow)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <b>Comment ça marche :</b> tout email ajouté ici peut se connecter immédiatement via Google Login,
          sans redéploiement. Pour retirer l&apos;accès, clique sur la corbeille — l&apos;utilisateur sera bloqué à sa
          prochaine tentative de connexion (les sessions actives restent valides jusqu&apos;à expiration).
        </div>
      </div>
    </div>
  );
}
